import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Loader2, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Field, Input, Textarea, Select, SectionTitle } from "@/components/FormBits";
import EnhanceableTextarea from "@/components/EnhanceableTextarea";
import { useWizardState } from "@/hooks/useWizardState";
import { api, fileUrl } from "@/lib/api";
import { setPendingSubmit } from "@/lib/pendingSubmits";
import { FLOWS } from "@/config/entryFlows";

/** Read a possibly-nested key like `type_data.foo` from a project object. */
function readPath(obj, path) {
  if (!path) return "";
  return path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj) ?? "";
}

/** Make an update patch for a possibly-nested key. Returns a partial object. */
function writePath(project, path, value) {
  const parts = path.split(".");
  if (parts.length === 1) return { [parts[0]]: value };
  const root = parts[0];
  const existing = { ...(project[root] || {}) };
  // walk into nested objects
  let cursor = existing;
  for (let i = 1; i < parts.length - 1; i++) {
    cursor[parts[i]] = { ...(cursor[parts[i]] || {}) };
    cursor = cursor[parts[i]];
  }
  cursor[parts[parts.length - 1]] = value;
  return { [root]: existing };
}

export default function SubmitShort({ flowKey }) {
  const flow = FLOWS[flowKey];
  const { draftToken } = useParams();
  const navigate = useNavigate();
  const { project, loading, saving, lastSaved, update, saveNow } = useWizardState(draftToken);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  if (loading || !project) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-500">
          <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" /> Loading your draft…
        </div>
      </Layout>
    );
  }

  const stepDef = flow.steps[step - 1];
  const total = flow.steps.length;
  const pct = Math.round((step / total) * 100);

  const validate = (n) => {
    const def = flow.steps[n - 1];
    const missing = [];
    for (const f of def.fields || []) {
      if (f.required && !String(readPath(project, f.key) || "").trim()) {
        missing.push(f.label);
      }
    }
    return missing;
  };

  const goTo = async (n) => {
    if (n > step) {
      const missing = validate(step);
      if (missing.length) return toast.error(`Please fill in: ${missing.join(", ")}`);
    }
    await saveNow();
    update({ current_step: n });
    setStep(n);
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  const onSubmit = async () => {
    // validate every step
    for (let i = 1; i <= total; i++) {
      const m = validate(i);
      if (m.length) { setStep(i); return toast.error(`Please fill in: ${m.join(", ")}`); }
    }
    await saveNow();
    setSubmitting(true);
    // Same optimistic pattern as the project wizard — navigate immediately and
    // let the Thank-You page resolve the background promise.
    localStorage.removeItem("rcw_draft_token");
    const submitPromise = api
      .post(`/projects/draft/${draftToken}/submit`, { hp_website: "" })
      .then(() => ({ ok: true }))
      .catch((e) => ({
        ok: false,
        detail: e?.response?.data?.detail || "Submission failed",
        status: e?.response?.status,
      }));
    setPendingSubmit(draftToken, submitPromise);
    navigate(`/thank-you?p=${draftToken}`);
  };

  const onSaveLater = async () => {
    await saveNow();
    try {
      await api.post(`/projects/draft/${draftToken}/save-and-send-link`);
      toast.success("Draft saved — a private link has been emailed.");
    } catch { toast.error("Could not email the link, but your draft is saved."); }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 rcw-mobile-pad-bottom">
        {/* progress header */}
        <div className="rcw-card p-4 sm:p-5 mb-5 sm:mb-6 sticky top-16 z-20 bg-white">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="rcw-label" data-testid="short-step-label">
                {flow.label} · Step {step} of {total}
              </div>
              <div className="font-display font-semibold text-base sm:text-lg text-gray-900 mt-0.5">
                {stepDef.title}
              </div>
            </div>
            <SaveBadge saving={saving} lastSaved={lastSaved} />
          </div>
          <div className="mt-3 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* step content */}
        <div className="rcw-card p-5 sm:p-8">
          {stepDef.eyebrow && (
            <SectionTitle eyebrow={stepDef.eyebrow} hint={stepDef.hint}>
              {stepDef.title}
            </SectionTitle>
          )}
          {!stepDef.eyebrow && (
            <SectionTitle eyebrow={stepDef.title} hint={stepDef.hint}>
              {step === 1 ? "Name it & tell us who you are" : stepDef.title}
            </SectionTitle>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(stepDef.fields || []).map((f) => (
              <FieldRenderer
                key={f.key}
                field={f}
                project={project}
                onChange={(v) => update(writePath(project, f.key, v))}
                flowLabel={flow.label}
                flowKey={flowKey}
                stepTitle={stepDef.title}
              />
            ))}
          </div>

          {stepDef.upload && (
            <div className="mt-6">
              <UploadBlock project={project} update={update} draftToken={draftToken} />
            </div>
          )}
        </div>
      </div>

      {/* sticky nav */}
      <div className="fixed sm:sticky bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 sm:mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex sm:hidden mb-2">
            {step < total ? (
              <button onClick={() => goTo(step + 1)} className="rcw-btn-amber w-full text-base" data-testid="short-next">
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={onSubmit} disabled={submitting} className="rcw-btn-amber w-full text-base" data-testid="short-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit
              </button>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={() => step > 1 && goTo(step - 1)} disabled={step === 1} className="rcw-btn-ghost text-sm">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <div className="flex items-center gap-2">
              <button onClick={onSaveLater} className="rcw-btn-ghost text-xs sm:text-base">
                <Save className="h-4 w-4" /> <span className="hidden sm:inline">Save & continue later</span><span className="sm:hidden">Save</span>
              </button>
              {step < total ? (
                <button onClick={() => goTo(step + 1)} className="hidden sm:inline-flex rcw-btn-amber text-base">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={onSubmit} disabled={submitting} className="hidden sm:inline-flex rcw-btn-amber text-base">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Submit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function FieldRenderer({ field, project, onChange, flowLabel, stepTitle, flowKey }) {
  const v = readPath(project, field.key) ?? "";
  const span = field.type === "textarea" ? "sm:col-span-2" : "";
  return (
    <div className={span}>
      <Field label={field.label} required={field.required}>
        {field.type === "textarea" ? (
          <EnhanceableTextarea
            value={v}
            onChange={(e) => onChange(e.target.value)}
            section={`${flowLabel} · ${stepTitle}`}
            fieldLabel={field.label}
            contextHint={field.placeholder || ""}
            entryType={flowKey}
            placeholder={field.placeholder || ""}
            testId={`short-${field.key.replace(/\./g, "-")}`}
          />
        ) : field.type === "select" ? (
          <Select options={field.options || []} value={v} onChange={(e) => onChange(e.target.value)} />
        ) : (
          <Input type={field.type || "text"} value={v} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ""} />
        )}
      </Field>
    </div>
  );
}

function SaveBadge({ saving, lastSaved }) {
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${saving ? "bg-gray-100 text-gray-600" : "bg-emerald-50 text-emerald-700"}`}>
      {saving ? <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</> : <><Check className="h-3 w-3" /> Saved{lastSaved ? <span className="text-gray-400 hidden sm:inline">· {lastSaved.toLocaleTimeString()}</span> : null}</>}
    </div>
  );
}

function UploadBlock({ project, update, draftToken }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const files = (project.files || []).filter((f) => !f.is_deleted);

  const handleFiles = async (fileList) => {
    setBusy(true);
    try {
      const accum = [...(project.files || [])];
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", "Other files");
        try {
          const { data } = await api.post(`/projects/draft/${draftToken}/files`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          accum.push(data);
          update({ files: [...accum] });
        } catch (e) {
          const msg = e?.response?.data?.detail || "Upload failed";
          toast.error(typeof msg === "string" ? msg : "Upload failed");
        }
      }
    } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const removeFile = async (path) => {
    update({ files: (project.files || []).map((f) => f.storage_path === path ? { ...f, is_deleted: true } : f) });
    try { await api.delete(`/projects/draft/${draftToken}/files/${path}`); } catch {}
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <label className="rcw-btn-amber cursor-pointer" data-testid="short-upload-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? "Uploading…" : "Choose files"}
          <input ref={inputRef} type="file" multiple className="hidden"
            onChange={(e) => handleFiles(e.target.files)} />
        </label>
        <div className="text-xs text-gray-500">Photos, PDFs, screenshots, video</div>
      </div>
      {files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {files.map((f) => {
            const isImage = (f.content_type || "").startsWith("image/");
            return (
              <div key={f.storage_path} className="rcw-card overflow-hidden">
                <div className="aspect-video bg-gray-100 relative">
                  {isImage ? (
                    <img src={fileUrl(f.storage_path)} alt={f.original_filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-xs px-2 text-center">
                      {f.original_filename}
                    </div>
                  )}
                  <button onClick={() => removeFile(f.storage_path)} className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white inline-flex items-center justify-center">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
